import { Dispatcher } from 'flux';
import async from 'async';

/**
 * @tutorial
 * This code predates the days of Redux's popular "single store" ideology, but
 * kind of recreates it by leveraging a first-in-first-out queue that drip
 * feeds our main flux "AppDispatcher" with action payloads to dispatch.
 *
 * Elsewhere in our codebase, we have many "Stores" will call
 * `AppDispatcher.register()` to register an additional callback function that
 * runs whenever a new action is dispatched from `AppDispatcher.dispatch()`.
 *
 * These callbacks, often named "dispatcherCallback" in the Store classes, work
 * in the same way a reducer in Redux would work. There's typically a
 * `switch` block that processes the action payload's `actionType` value,
 * and based on that value, it will enact a certain business logic, or – if
 * there's no matching case – do nothing.
 *
 * From this point, however, the general behaviour of our Stores differ drastically
 * from a Redux Reducer.
 *
 * For each case in a Store's dispatcherCallback, the Store will call `emitChange()` on iself.
 * which results a `CHANGE` event being emitted on that store.
 *
 * Our React Components – which aren't actually connected to the Stores can
 * loosely subscribe to this `CHANGE` event on the store.
 * Typically, when the component mounts, they will call `<StoreName>.addChangeListener(<callback>)`
 * to run a callback function, when the `CHANGE` event is emitted.
 *
 * It's important to note that we're not subscribing to when our Flux `AppDispatcher` dispatches an action.
 * The component is just going to execute a callback when the particular Store
 * emits a `CHANGE` event on itself.
 *
 * The React Component doesn't get any information about what exactly has changed,
 * It also doesn't get any new values from the store passed into the callback.
 *
 * If we want to respond to the change event, we must manually get these values ourselves,
 * typically by calling functions in our Store to retrieve potentially different values to
 * re-render with. Some components even carry out business-logic in the
 * callback function given to `<Store>.addChangeListener()`, which sadly
 * blurs the line between our UI code and Business Logic. Try to avoid doing this.
 *
 * A single React Component can add change listeners to multiple Stores, and can
 * also add multiple change listeners to the same Store, in order to execute
 * multiple callback functions in response to a single `CHANGE` event.
 *
 * This comes with the caveat that developers must remember to *remove* their
 * registered change listener when they are unmounting their component, otherwise
 * the callback may still fire when the Store emits a `CHANGE` event after the
 * component instance no-longer exists, which may lead to unexpected behaviour,
 * or quite likely crash because the callback refers to an instance that's no
 * longer defined.
 *
 * This architecture is inferior to a good Redux setup in several ways.
 * The most obvious thing is that our Store classes are actually extended
 * instances of the Node.js Core's `event-emitter` module, which doesn't exist
 * in the browser, and you won't find it in our package.json dependencies.
 * Under-the-hood, webpack 4 is adding in a browserified polyfill module to keep
 * things working. In webpack 5, we'd need to do this explicitly to keep things
 * working.
 *
 * Another Reason is that our React Components have to work a lot harder to manage
 * change than with react-redux. When a Store emits `CHANGE`, we have know way
 * of knowing exactly what has changed, and have to re-do a lot of work that
 * may be unnecessary in order to update our component correctly.
 * With react-redux. We could use the `connect()` HOC to provide store values as
 * props to our React component, which update whenever they change the Redux Store.
 * It's typically just a simple matter of letting the component re-render with
 * updated props in order to reflect the change, and cleanup is handled for us.
 *
 * Lastly, you'll see that this architecture is not the best example of using Flux.
 * We deliberately constrain how actions are dispatched using a queue. Our
 * actions don't follow the Flux Standard Actions pattern, as we have a top level
 * "source" key instead of just making the entire paylaod the body of our action payload.
 *
 * We may be able to remove `source` as it may not be useful, but this requires
 * investigation.
 *
 * Hopefully, this will help arm you with a better understanding of how state
 * currently works within `internet-banking-webapp`, and help you iterate the
 * codebase forward.
 */

// Queue the actions. Implementation discussion found at https://github.com/facebook/flux/issues/106
const queue = async.queue(({ source, action }, callback) => {
  AppDispatcher.dispatch({
    source /* either VIEW_ACTION or SERVER_ACTION -- no bearing on anything either way...*/,
    action,
  });
  callback();
}, 1); // Only one worker, one event at a time

function queueAction(sourcePrefix, action) {
  queue.push({ source: sourcePrefix + '_ACTION', action });
}

const AppDispatcher = Object.assign(new Dispatcher(), {
  handleServerAction(action) {
    queueAction('SERVER', action);
  },
  handleViewAction(action) {
    queueAction('VIEW', action);
  },
});

export default AppDispatcher;
